'use client';

import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import { useCallback, useEffect, useId, useRef, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';

import { Container } from '@/components/layout/Container';
import { OverlayResourceDisclosure } from '@/components/patterns/OverlayResourceDisclosure';
import { OverlayThreatGrid } from '@/components/patterns/OverlayThreatGrid';
import { ChevronIcon } from '@/components/primitives/icons/ChevronIcon';
import { CloseIcon } from '@/components/primitives/icons/CloseIcon';
import { Media } from '@/components/primitives/Media';
import {
  GA_OVERLAY_CLOSE_METHOD,
  GA_OVERLAY_NAV_METHOD,
  type GaOverlayCloseMethod,
  type GaOverlayNavMethod,
} from '@/constants/analytics';
import {
  OVERLAY_CLOSE_BAR_BLOCK_SIZE,
  OVERLAY_ENABLER_MEDIA_HEIGHT,
  OVERLAY_ENABLER_MEDIA_WIDTH,
} from '@/constants/overlay';
import { useFocusTrap } from '@/hooks/useFocusTrap';
import { useInertBackground } from '@/hooks/useInertBackground';
import { useScrollLock } from '@/hooks/useScrollLock';
import { useSwipeNavigation } from '@/hooks/useSwipeNavigation';
import underlineHoverStyles from '@/styles/underlineHover.module.css';

import styles from './Overlay.module.css';

import type { ResolvedOverlayEntry } from '@/content/data/overlay-entries';

const MOTION_OVERLAY_OPEN = { duration: 0.5, ease: [0.4, 0, 0.2, 1] as const };
const MOTION_OVERLAY_SLIDE = { duration: 0.35, ease: [0.4, 0, 0.2, 1] as const };
const SLIDE_OFFSET_PX = 48;

export type OverlayLabels = {
  close: string;
  previous: string;
  next: string;
  keyThreatsHeading: string;
  resources: string;
};

type OverlayProps = {
  open: boolean;
  entry: ResolvedOverlayEntry;
  labels: OverlayLabels;
  previousTitle: string;
  nextTitle: string;
  slideDirection: 1 | -1;
  onClose: (method: GaOverlayCloseMethod) => void;
  onPrevious: (method: GaOverlayNavMethod) => void;
  onNext: (method: GaOverlayNavMethod) => void;
};

function OverlayBody({ body }: { body: ResolvedOverlayEntry['body'] }) {
  const paragraphs = typeof body === 'string' ? [body] : body;

  return (
    <div className={styles.body}>
      {paragraphs.map((paragraph, index) => (
        <p key={index}>{paragraph}</p>
      ))}
    </div>
  );
}

function getSlideOffset(direction: 1 | -1): number {
  const isRtl =
    typeof document !== 'undefined' && document.documentElement.dir === 'rtl';
  const sign = isRtl ? -1 : 1;
  return direction * sign * SLIDE_OFFSET_PX;
}

const slideVariants = {
  enter: (direction: 1 | -1) => ({
    opacity: 0,
    x: getSlideOffset(direction),
  }),
  center: { opacity: 1, x: 0 },
  exit: (direction: 1 | -1) => ({
    opacity: 0,
    x: -getSlideOffset(direction),
  }),
};

const reducedSlideVariants = {
  enter: { opacity: 0, x: 0 },
  center: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: 0 },
};

/**
 * Full-bleed bottom-sheet overlay (Figma `708:96935`). Portaled to `document.body`
 * with backdrop scrim, scroll-lock, focus trap, and inert background. Only mounts
 * entry content while `open`.
 */
export function Overlay({
  open,
  entry,
  labels,
  previousTitle,
  nextTitle,
  slideDirection,
  onClose,
  onPrevious,
  onNext,
}: OverlayProps) {
  const titleId = useId();
  const shellRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const reducedMotion = useReducedMotion();
  const hasThreatGrid = Boolean(entry.threats?.length);
  const hasMedia = Boolean(entry.mediaProps) && !hasThreatGrid;
  const panelBodyClass = hasThreatGrid
    ? styles.panelBody
    : hasMedia
      ? `${styles.panelBody} ${styles.panelBodyMedia}`
      : `${styles.panelBody} ${styles.panelBodySingle}`;

  useScrollLock(open);
  useInertBackground(open);

  useFocusTrap({
    active: open,
    containerRef: shellRef,
    initialFocusRef: closeRef,
    onEscape: () => onClose(GA_OVERLAY_CLOSE_METHOD.escape),
  });

  const swipeHandlers = useSwipeNavigation({
    enabled: open,
    onPrevious: () => onPrevious(GA_OVERLAY_NAV_METHOD.swipe),
    onNext: () => onNext(GA_OVERLAY_NAV_METHOD.swipe),
  });

  const handleArrowKeys = useCallback(
    (event: KeyboardEvent) => {
      if (!open) return;
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        onPrevious(GA_OVERLAY_NAV_METHOD.keyboard);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        onNext(GA_OVERLAY_NAV_METHOD.keyboard);
      }
    },
    [onNext, onPrevious, open],
  );

  useEffect(() => {
    if (!open) return;
    document.addEventListener('keydown', handleArrowKeys);
    return () => document.removeEventListener('keydown', handleArrowKeys);
  }, [handleArrowKeys, open]);

  if (typeof document === 'undefined') return null;

  const shellMotion = reducedMotion
    ? { initial: { opacity: 1 }, animate: { opacity: 1 }, exit: { opacity: 0 } }
    : {
        initial: { opacity: 0 },
        animate: { opacity: 1 },
        exit: { opacity: 0 },
        transition: { duration: 0.25 },
      };

  const sheetMotion = reducedMotion
    ? {
        initial: { opacity: 1, y: 0 },
        animate: { opacity: 1, y: 0 },
        exit: { opacity: 0 },
      }
    : {
        initial: { opacity: 0, y: '100%' },
        animate: { opacity: 1, y: 0 },
        exit: { opacity: 0, y: '100%' },
        transition: MOTION_OVERLAY_OPEN,
      };

  const activeSlideVariants = reducedMotion ? reducedSlideVariants : slideVariants;
  const sheetStyle = {
    '--overlay-close-bar-block-size': OVERLAY_CLOSE_BAR_BLOCK_SIZE,
  } as CSSProperties;

  return createPortal(
    <AnimatePresence mode="wait">
      {open ? (
        <motion.div
          key="overlay-shell"
          ref={shellRef}
          className={styles.overlay}
          data-overlay-portal=""
          {...shellMotion}
        >
          <motion.button
            type="button"
            className={styles.backdrop}
            tabIndex={-1}
            onClick={() => onClose(GA_OVERLAY_CLOSE_METHOD.backdrop)}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={reducedMotion ? { duration: 0 } : MOTION_OVERLAY_OPEN}
          />

          <motion.div
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            className={styles.sheet}
            style={sheetStyle}
            {...swipeHandlers}
            {...sheetMotion}
          >
            <div className={styles.closeBar}>
              <button
                ref={closeRef}
                type="button"
                className={styles.closeControl}
                aria-label={labels.close}
                onClick={() => onClose(GA_OVERLAY_CLOSE_METHOD.button)}
              >
                <span className={styles.closeLabel}>{labels.close}</span>
                <CloseIcon size={20} className={styles.closeIcon} />
              </button>
            </div>

            <div className={styles.panelInner}>
              <div className={panelBodyClass} data-lenis-prevent="">
                <Container className={styles.bodyContainer}>
                  <AnimatePresence mode="wait" custom={slideDirection} initial={false}>
                    <motion.div
                      key={entry.id}
                      className={styles.slidePane}
                      custom={slideDirection}
                      variants={activeSlideVariants}
                      initial="enter"
                      animate="center"
                      exit="exit"
                      transition={reducedMotion ? { duration: 0 } : MOTION_OVERLAY_SLIDE}
                    >
                      <div className={styles.contentColumn}>
                        {/* eslint-disable-next-line @next/next/no-img-element -- decorative botanical (ADR-004) */}
                        <img
                          src="/svg/overlay-botanical.png"
                          alt=""
                          className={styles.botanical}
                          width={400}
                          height={400}
                          decoding="async"
                          aria-hidden
                        />

                        <div className={styles.contentInner}>
                          <h2 id={titleId} className={styles.title}>
                            {entry.title}
                          </h2>
                          <OverlayBody body={entry.body} />
                        </div>

                        <OverlayResourceDisclosure
                          key={entry.id}
                          articles={entry.articles}
                          caseStudies={entry.caseStudies}
                          label={labels.resources}
                        />
                      </div>

                      {hasThreatGrid && entry.threats ? (
                        <aside className={styles.threatsColumn}>
                          <h3 className={styles.threatsHeading}>
                            {labels.keyThreatsHeading}
                          </h3>
                          <OverlayThreatGrid threats={entry.threats} />
                        </aside>
                      ) : null}

                      {hasMedia && entry.mediaProps ? (
                        <div className={styles.mediaColumn}>
                          <Media
                            sources={entry.mediaProps.sources}
                            sizes={entry.mediaProps.sizes}
                            alt={entry.mediaProps.alt}
                            width={OVERLAY_ENABLER_MEDIA_WIDTH}
                            height={OVERLAY_ENABLER_MEDIA_HEIGHT}
                            className={styles.mediaImage}
                            loading="eager"
                          />
                        </div>
                      ) : null}
                    </motion.div>
                  </AnimatePresence>
                </Container>
              </div>

              <nav className={styles.modalNav} aria-label={labels.previous}>
                <Container className={styles.navInner}>
                  <button
                    type="button"
                    className={`${styles.navButton} ${underlineHoverStyles.parent}`}
                    onClick={() => onPrevious(GA_OVERLAY_NAV_METHOD.control)}
                    aria-label={`${labels.previous}: ${previousTitle}`}
                    title={previousTitle}
                  >
                    <ChevronIcon size={20} className={styles.navButtonIconStart} />
                    <span
                      className={`${styles.navButtonTitle} ${underlineHoverStyles.root} ${underlineHoverStyles.fromParent}`}
                    >
                      {previousTitle}
                    </span>
                  </button>
                  <button
                    type="button"
                    className={`${styles.navButton} ${underlineHoverStyles.parent}`}
                    onClick={() => onNext(GA_OVERLAY_NAV_METHOD.control)}
                    aria-label={`${labels.next}: ${nextTitle}`}
                    title={nextTitle}
                  >
                    <span
                      className={`${styles.navButtonTitle} ${underlineHoverStyles.root} ${underlineHoverStyles.fromParent}`}
                    >
                      {nextTitle}
                    </span>
                    <ChevronIcon size={20} className={styles.navButtonIconEnd} />
                  </button>
                </Container>
              </nav>
            </div>
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>,
    document.body,
  );
}
